import React, { useState, useMemo, useEffect } from 'react'
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  ColumnLayout,
  Badge,
  Cards,
  Button,
  Spinner,
  ExpandableSection
} from '@cloudscape-design/components'
import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useAmplifyAuth } from '../../../providers/AmplifyAuthProvider'

interface SelectedAsset {
  _id: string
  name: string
  type: 'factory' | 'production_line' | 'equipment_component'
  status: string
  external_asset_id: string
  sitewise_asset_id?: string
  properties?: Array<{
    _id: string
    name: string
    unit?: string
    data_type: string
    property_config?: string
    external_property_id?: string
  }>
}

interface AssetPropertiesProps {
  asset: SelectedAsset
}

export default function AssetProperties({ asset }: AssetPropertiesProps) {
  const [loading, setLoading] = useState(false)
  const { user } = useAmplifyAuth()
  
  // Fetch asset properties from API
  const assetWithProperties = useQuery(
    (api as any).components.assets.index.getAssetDetailsWithProperties,
    user ? {
      sessionId: user.sessionId || 'temp-session',
      assetId: asset._id,
      assetType: asset.type
    } : 'skip'
  )



  // Format property name for display
  const formatPropertyName = (name: string | undefined) => {
    if (!name) return 'Unknown Property'
    return name.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  // Get category display info
  const getCategoryInfo = (categoryKey: string) => {
    switch (categoryKey) {
      case 'measurements':
        return { 
          title: 'Measurements', 
          badgeColor: 'blue' as const
        }
      case 'attributes':
        return { 
          title: 'Attributes', 
          badgeColor: 'grey' as const
        }
      case 'metrics':
        return { 
          title: 'Metrics', 
          badgeColor: 'green' as const
        }
      case 'transforms':
        return { 
          title: 'Transforms', 
          badgeColor: 'red' as const
        }
      case 'other':
        return { 
          title: 'Other', 
          badgeColor: 'grey' as const
        }
      default:
        return { 
          title: 'Properties', 
          badgeColor: 'blue' as const
        }
    }
  }

  // Get property icon
  const getPropertyIcon = (name: string) => {
    if (!name) return '📊'
    const lowerName = name.toLowerCase()
    if (lowerName.includes('torque')) return '🔧'
    if (lowerName.includes('speed') || lowerName.includes('rpm')) return '⚡'
    if (lowerName.includes('power') || lowerName.includes('energy')) return '⚡'
    if (lowerName.includes('temp') || lowerName.includes('temperature')) return '🌡️'
    if (lowerName.includes('pressure')) return '💨'
    if (lowerName.includes('flow') || lowerName.includes('rate')) return '🌊'
    if (lowerName.includes('level') || lowerName.includes('height')) return '📏'
    if (lowerName.includes('vibration')) return '📳'
    if (lowerName.includes('current') || lowerName.includes('voltage')) return '⚡'
    return '📊'
  }

  // Get categorized properties
  const categorizedProperties = useMemo(() => {
    return assetWithProperties?.categorizedProperties || {
      measurements: [],
      attributes: [],
      metrics: [],
      transforms: [],
      other: []
    }
  }, [assetWithProperties])

  // Get total property count
  const totalProperties = useMemo(() => {
    return Object.values(categorizedProperties).reduce((total, props) => total + props.length, 0)
  }, [categorizedProperties])

  if (!asset) {
    return (
      <Container>
        <Box textAlign="center" color="inherit">
          <b>No asset selected</b>
          <Box variant="p" color="inherit">
            Select an asset from the hierarchy to view its properties.
          </Box>
        </Box>
      </Container>
    )
  }

  return (
    <Container>
      <SpaceBetween size="m">
        <Header variant="h3" actions={
          <Button 
            iconName="refresh" 
            loading={loading}
            onClick={() => setLoading(true)}
          >
            Refresh
          </Button>
        }>
          Properties ({totalProperties})
        </Header>
        
        {totalProperties > 0 && (
          <SpaceBetween size="l">
            {Object.entries(categorizedProperties).map(([categoryKey, properties]) => {
              if (properties.length === 0) return null;
              const categoryInfo = getCategoryInfo(categoryKey);
              
              return (
                <ExpandableSection
                  key={categoryKey}
                  headerText={categoryInfo.title}
                  headerActions={<Badge color={categoryInfo.badgeColor}>{properties.length}</Badge>}
                  defaultExpanded={true}
                >
                  <SpaceBetween size="xs">
                    {properties.map((prop: any) => (
                      <div key={prop._id || prop.id} style={{ 
                        padding: '12px',
                        border: '1px solid #e1e8ed',
                        borderRadius: '6px',
                        backgroundColor: '#fafbfc'
                      }}>
                        <ColumnLayout columns={4} variant="text-grid">
                          <div>
                            <Box variant="awsui-key-label">Name</Box>
                            <span style={{ fontWeight: '500' }}>{formatPropertyName(prop.name)}</span>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">Type</Box>
                            <Badge color="blue">{prop.data_type || prop.dataType}</Badge>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">Value</Box>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '16px', fontWeight: '600', color: '#0972d3' }}>--</span>
                              {prop.unit && prop.unit !== 'units' && (
                                <span style={{ color: '#5f6b7a' }}>{prop.unit}</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <Box variant="awsui-key-label">ID</Box>
                            <span style={{ fontSize: '12px', color: '#5f6b7a', fontFamily: 'monospace' }}>
                              {prop.mapping?.external_property_id || 'N/A'}
                            </span>
                          </div>
                        </ColumnLayout>
                      </div>
                    ))}
                  </SpaceBetween>
                </ExpandableSection>
              );
            })}
          </SpaceBetween>
        )}

        {/* Loading State */}
        {!assetWithProperties && (
          <Box textAlign="center" color="inherit">
            <Spinner size="large" />
            <Box variant="p" color="inherit" style={{ marginTop: '16px' }}>
              Loading properties...
            </Box>
          </Box>
        )}

        {/* Empty State */}
        {assetWithProperties && totalProperties === 0 && (
          <Box textAlign="center" color="inherit">
            <b>No properties found</b>
            <Box variant="p" color="inherit">
              This asset has no properties configured yet.
            </Box>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  )
}